import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import {
  Checkbox,
  Col,
  Divider,
  Form,
  Radio,
  Row,
  Select,
  Tooltip,
  Typography,
} from 'antd';

import { PageProps } from '@/types/PageProps';
import { FloatingFormItem, Group, SaveButton } from '@/components/_common';

import {
  ProductPriceChangeOnPurchase,
  UpdateMatrixSettingsRequest,
} from '@/types/Register';
import {
  useMatrix,
  useMatrixUpdate,
} from '@/core/domain/enterprise/Matrix/Matrix.hooks';
import { useEffect } from 'react';
import { useWindowSize } from '@/hooks';
import { Info } from 'react-feather';

const { Title } = Typography;

interface MovementSettingsFormValues {
  productPriceChangeOnPurchase: ProductPriceChangeOnPurchase;
  updateNcmAttributesOnExternalImports: boolean;
  purchaseUseWorkOrderCost: boolean;
  shouldUpdateOriginOnPurchaseAuthorization: boolean;
  isDamageSelectedFirst: string;
  canViewMultipleCompaniesStockInDirectSale: boolean;
  shouldUpdateWorkOrderPartManufacturerAfterPurchase: boolean;
  shouldUpdateKmOnVehicle: boolean;
}

function MovementsSettingsPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<MovementSettingsFormValues>();
  const { width } = useWindowSize();

  const { data } = useMatrix();
  const mutationEdit = useMatrixUpdate();

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        productPriceChangeOnPurchase:
          data.matrixSettings.productPriceChangeOnPurchase,
        updateNcmAttributesOnExternalImports:
          data.matrixSettings.updateNcmAttributesOnExternalImports,
        purchaseUseWorkOrderCost: data.matrixSettings.purchaseUseWorkOrderCost,
        shouldUpdateOriginOnPurchaseAuthorization:
          data.matrixSettings.shouldUpdateOriginOnPurchaseAuthorization,
        isDamageSelectedFirst: JSON.stringify(
          data.matrixSettings.isDamageSelectedFirst,
        ),
        canViewMultipleCompaniesStockInDirectSale:
          data.matrixSettings.canViewMultipleCompaniesStockInDirectSale,
        shouldUpdateWorkOrderPartManufacturerAfterPurchase:
          data.matrixSettings
            .shouldUpdateWorkOrderPartManufacturerAfterPurchase,
        shouldUpdateKmOnVehicle: data.matrixSettings.shouldUpdateKmOnVehicle,
      });
    }
  }, [data]);

  const handleEditSettings = async () => {
    const values = await form.validateFields();

    if (data) {
      const obj: UpdateMatrixSettingsRequest = {
        id: data.id,
        matrixSettings: {
          ...values,
          id: data.matrixSettings.id,
          isDamageSelectedFirst: values.isDamageSelectedFirst
            ? JSON.parse(values.isDamageSelectedFirst)
            : undefined,
        },
      };

      mutationEdit.mutateAsync(obj);
    }
  };

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <div className="page">
        <div className="page-header">
          <Title level={4}>Compras</Title>

          {!(width < 992) && (
            <SaveButton
              onClick={handleEditSettings}
              loading={mutationEdit.isLoading}
            >
              {intl.formatMessage({
                id: 'commons.form.button.save',
              })}
            </SaveButton>
          )}
        </div>

        <Divider />

        <Form form={form} style={{ marginTop: '24px' }}>
          <Row gutter={[16, 12]}>
            <Col sm={24}>
              <Title level={5}>
                Alterar o preço do produto ao autorizar uma compra:
              </Title>

              <Form.Item name="productPriceChangeOnPurchase">
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  size="large"
                >
                  <Radio value={ProductPriceChangeOnPurchase.neverChange}>
                    Nunca alterar
                  </Radio>
                  <Radio value={ProductPriceChangeOnPurchase.askBeforeChange}>
                    Perguntar antes de alterar
                  </Radio>
                  <Radio value={ProductPriceChangeOnPurchase.alwaysChange}>
                    Sempre alterar de forma automática
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="purchaseUseWorkOrderCost"
                valuePropName="checked"
              >
                <Checkbox style={{ marginTop: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    Enviar o preço de custo dos itens da OS ao vincular na
                    compra
                    <Tooltip title="Quando o preço de custo dos itens é informado no orçamento, ele é enviado para a compra ao vincular o orçamento à compra.">
                      <Info
                        size={20}
                        style={{ marginLeft: '8px', cursor: 'pointer' }}
                      />
                    </Tooltip>
                  </div>
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="shouldUpdateWorkOrderPartManufacturerAfterPurchase"
                valuePropName="checked"
              >
                <Checkbox style={{ marginTop: '12px' }}>
                  Atualizar o fabricante do item do orçamento conforme o item da
                  compra vinculada
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="shouldUpdateOriginOnPurchaseAuthorization"
                valuePropName="checked"
              >
                <Checkbox style={{ marginTop: '12px' }}>
                  Atualizar automaticamente o campo origem no cadastro da peça
                  ao autorizar compra
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Title level={4}>Importações</Title>

              <Form.Item
                name="updateNcmAttributesOnExternalImports"
                valuePropName="checked"
              >
                <Checkbox style={{ marginTop: '12px' }}>
                  Atualiza NCM na importação de XML de compra
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={24} xl={12}>
              <Title level={4}>Checklist</Title>

              <FloatingFormItem
                label="Ordem de seleção da avaria"
                name="isDamageSelectedFirst"
              >
                <Select
                  options={[
                    {
                      label:
                        'Selecionar a avaria primeiro e depois o local da avaria',
                      value: 'false',
                    },
                    {
                      label:
                        'Selecionar o local da avaria primeiro e depois a avaria',
                      value: 'true',
                    },
                  ]}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24}>
              <Title level={4}>Venda direta</Title>

              <Form.Item
                name="canViewMultipleCompaniesStockInDirectSale"
                valuePropName="checked"
              >
                <Checkbox style={{ marginTop: '12px' }}>
                  Visualizar o estoque de todas as empresas na seleção de peça
                </Checkbox>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Title level={4}>Veículo</Title>

              <Form.Item name="shouldUpdateKmOnVehicle" valuePropName="checked">
                <Checkbox style={{ marginTop: '12px' }}>
                  <Group>
                    <span>
                      Atualizar automaticamente o campo 'KM' do veículo usando o
                      valor do campo 'KM de entrada' do orçamento.
                    </span>

                    <Tooltip title="Quando ativado, ao criar o orçamento, o campo 'KM de entrada' é preenchido automaticamente usando o valor do 'KM' do veículo selecionado.">
                      <Info
                        size={20}
                        style={{ marginLeft: '8px', cursor: 'pointer' }}
                      />
                    </Tooltip>
                  </Group>
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {width < 992 && (
          <SaveButton
            block
            onClick={handleEditSettings}
            loading={mutationEdit.isLoading}
          >
            {intl.formatMessage({
              id: 'commons.form.button.save',
            })}
          </SaveButton>
        )}
      </div>
    </>
  );
}

export default MovementsSettingsPage;
